import React, { useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';

const LightModeCanvas = () => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const materialRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());
  const waterBuffersRef = useRef(null);
  const waterTextureRef = useRef(null);
  const animationIdRef = useRef(null);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const mouseThrottleTimeRef = useRef(0);

  const waterSettings = useMemo(() => ({
    resolution: 256,
    damping: 0.913,
    tension: 0.02,
    rippleStrength: 0.2,
    mouseIntensity: 1.2,
    clickIntensity: 3.0,
    rippleRadius: 8,
    splatForce: 50000,
    splatThickness: 0.1,
    vorticityInfluence: 0.2,
    swirlIntensity: 0.2,
    pressure: 0.3,
    velocityDissipation: 0.08,
    densityDissipation: 1.0,
    displacementScale: 0.01,
    spiralIntensity: 0.2,
    waveHeight: 0.01,
    motionDecay: 0.08,
    rippleDecay: 1.0,
    rippleSize: 0.1
  }), []);

  const vertexShader = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const fragmentShader = `
    uniform float u_time;
    uniform vec2 u_resolution;
    uniform float u_speed;
    uniform sampler2D u_waterTexture;
    uniform float u_waterStrength;
    uniform float u_ripple_time;
    uniform vec2 u_ripple_position;
    uniform float u_ripple_strength;
    uniform int u_gradientTheme;
    
    varying vec2 vUv;

    // Pearl Bloom - soft, ethereal light effect
    vec4 pearlBloom(vec2 u, float t) {
      float w = 0.0;
      float a = 0.0, d = 0.0, i = 0.0;
      for (; i < 5.; d += sin(i++ * u.y + a)) a += cos(i - d + t * 0.05 - a * u.x);
      w = (a + d) * 0.2;
      vec2 off = vec2(cos(w), sin(w)) * 0.1;
      float f = 0.5 + 0.5 * cos(a + d);
      vec3 base = vec3(0.92 + f * 0.08);
      float edge = smoothstep(0.1, 0.0, fwidth(f * 5.0));
      vec3 spec = 0.5 + 0.5 * cos(f * 10.0 + vec3(0.0, 2.0, 4.0));
      return vec4(mix(base, spec * 0.3 + 0.7, pow(edge, 2.0)), 1.0);
    }

    // Dreamscape Pastel - soft flowing pastels
    vec4 dreamscapePastel(vec2 u, float t) {
      float a = 0.0, d = 0.0, i = 0.0;
      for (; i < 6.; d += sin(i++ * u.y + a))
         a += cos(i - d + 0.04 * t - a * u.x);
      vec3 c1 = vec3(0.98, 0.90, 0.93);
      vec3 c2 = vec3(0.90, 0.95, 0.98);
      vec3 c3 = vec3(0.95, 0.90, 0.98);
      vec3 c = mix(c1, c2, 0.5 + 0.5 * cos(a + d));
      c = mix(c, c3, 0.5 + 0.5 * sin(d));
      return vec4(c, 1.0);
    }

    // Liquid Crystal - the main effect similar to your example
    vec4 liquidCrystal(vec2 u, float t) {
      u *= 1.2;
      float a = 0.0, d = 0.0, i = 0.0;
      for (; i < 9.; d += cos(i++ * u.y + a))
         a += sin(i * 0.5 - d + 0.1 * t - a * u.x);
      vec3 c = 0.6 + 0.4 * cos(vec3(0.0, 2.1, 4.2) + a + d);
      float gloss = pow(smoothstep(0.6, 1.0, sin(d * 2.0 + a)), 10.0);
      c += vec3(0.8) * gloss;
      return vec4(clamp(c, 0.0, 1.0), 1.0);
    }

    // Soft Aurora - gentle flowing colors
    vec4 softAurora(vec2 u, float t) {
      float a = 0.0, d = 0.0, i = 0.0;
      for (; i < 8.; d += sin(i++ * u.y + a))
         a += sin(i - d + 0.08 * t - a * u.x);
      vec3 c1 = vec3(0.95, 0.97, 1.0);
      vec3 c2 = vec3(0.9, 0.95, 0.98);
      vec3 c3 = vec3(0.93, 0.9, 0.97);
      float v = 0.5 + 0.5 * cos(a + d);
      vec3 c = mix(c1, c2, v);
      c = mix(c, c3, 0.5 + 0.5 * sin(d * 0.5));
      return vec4(c, 1.0);
    }

    vec4 getGradientColor(vec2 u, float t, int theme) {
      if (theme == 0) return pearlBloom(u, t);
      else if (theme == 1) return dreamscapePastel(u, t);
      else if (theme == 2) return liquidCrystal(u, t);
      else return softAurora(u, t);
    }

    void main() {
      vec2 r = u_resolution;
      vec2 FC = gl_FragCoord.xy;
      vec2 screenP = (FC.xy * 2.0 - r) / r.y;
      
      // Get water height for distortion
      vec2 wCoord = vec2(FC.x / r.x, FC.y / r.y);
      float waterHeight = texture2D(u_waterTexture, wCoord).r;
      
      // Calculate water gradient for stronger refraction effect
      float step = 1.0 / r.x;
      vec2 waterGrad = vec2(
        texture2D(u_waterTexture, vec2(wCoord.x + step, wCoord.y)).r - 
        texture2D(u_waterTexture, vec2(wCoord.x - step, wCoord.y)).r,
        texture2D(u_waterTexture, vec2(wCoord.x, wCoord.y + step)).r - 
        texture2D(u_waterTexture, vec2(wCoord.x, wCoord.y - step)).r
      ) * u_waterStrength * 15.0; // Increased multiplier for more visible effect
      
      // Apply water distortion to UV coordinates
      vec2 gradientUV = screenP;
      gradientUV += waterGrad * 0.5; // Strong UV distortion from water
      gradientUV += vec2(waterHeight * u_waterStrength * 0.8); // Additional displacement
      
      // Click ripple effect
      float rippleTime = u_time - u_ripple_time;
      vec2 ripplePos = u_ripple_position * r;
      float rippleDist = distance(FC.xy, ripplePos);
      
      float clickRipple = 0.0;
      if (rippleTime < 3.0 && rippleTime > 0.0) {
        float rippleRadius = rippleTime * 200.0;
        float rippleWidth = 40.0;
        float rippleDecay = 1.0 - rippleTime / 3.0;
        clickRipple = exp(-abs(rippleDist - rippleRadius) / rippleWidth) * rippleDecay * u_ripple_strength;
        
        // Add concentric ripple waves
        float waveCount = 3.0;
        for (float w = 1.0; w <= waveCount; w++) {
          float waveRadius = rippleTime * 200.0 * (1.0 - w * 0.2);
          clickRipple += exp(-abs(rippleDist - waveRadius) / (rippleWidth * 0.7)) * rippleDecay * u_ripple_strength * 0.5;
        }
      }
      
      // Combine all water effects
      float totalWaterEffect = waterHeight * u_waterStrength + clickRipple * 0.3;
      gradientUV += vec2(totalWaterEffect * 0.6, totalWaterEffect * 0.5);
      
      // Modify time based on water for more organic movement
      float modifiedTime = u_time * u_speed + totalWaterEffect * 3.0;
      
      // Get the gradient color with all distortions applied
      vec4 gradientColor = getGradientColor(gradientUV, modifiedTime, u_gradientTheme);
      
      // Add subtle highlight based on water surface angle
      float highlight = clamp(length(waterGrad) * 2.0, 0.0, 0.3);
      gradientColor.rgb += highlight;
      
      gl_FragColor = gradientColor;
    }
  `;

  const initWaterBuffers = (resolution) => {
    const buffers = {
      current: new Float32Array(resolution * resolution),
      previous: new Float32Array(resolution * resolution),
      velocity: new Float32Array(resolution * resolution * 2),
      vorticity: new Float32Array(resolution * resolution),
      pressure: new Float32Array(resolution * resolution)
    };

    for (let i = 0; i < resolution * resolution; i++) {
      buffers.current[i] = 0.0;
      buffers.previous[i] = 0.0;
      buffers.velocity[i * 2] = 0.0;
      buffers.velocity[i * 2 + 1] = 0.0;
      buffers.vorticity[i] = 0.0;
      buffers.pressure[i] = 0.0;
    }

    return buffers;
  };

  const updateWaterSimulation = () => {
    if (!waterBuffersRef.current) return;
    
    const { current, previous, velocity, vorticity } = waterBuffersRef.current;
    const { damping, resolution, tension, vorticityInfluence } = waterSettings;
    const safeTension = Math.min(tension, 0.05);
    const velocityDissipation = waterSettings.motionDecay;
    const densityDissipation = waterSettings.rippleDecay;
    const safeVorticityInfluence = Math.min(Math.max(vorticityInfluence, 0.0), 0.5);

    // Apply velocity dissipation
    for (let i = 0; i < resolution * resolution * 2; i++) {
      velocity[i] *= 1.0 - velocityDissipation;
    }

    // Calculate vorticity
    for (let i = 1; i < resolution - 1; i++) {
      for (let j = 1; j < resolution - 1; j++) {
        const index = i * resolution + j;
        const left = velocity[(index - 1) * 2 + 1];
        const right = velocity[(index + 1) * 2 + 1];
        const bottom = velocity[(index - resolution) * 2];
        const top = velocity[(index + resolution) * 2];
        vorticity[index] = (right - left - (top - bottom)) * 0.5;
      }
    }

    // Apply vorticity confinement
    if (safeVorticityInfluence > 0.001) {
      for (let i = 1; i < resolution - 1; i++) {
        for (let j = 1; j < resolution - 1; j++) {
          const index = i * resolution + j;
          const velIndex = index * 2;
          const left = Math.abs(vorticity[index - 1]);
          const right = Math.abs(vorticity[index + 1]);
          const bottom = Math.abs(vorticity[index - resolution]);
          const top = Math.abs(vorticity[index + resolution]);
          const gradX = (right - left) * 0.5;
          const gradY = (top - bottom) * 0.5;
          const length = Math.sqrt(gradX * gradX + gradY * gradY) + 1e-5;
          const safeVorticity = Math.max(-1.0, Math.min(1.0, vorticity[index]));
          const forceX = (gradY / length) * safeVorticity * safeVorticityInfluence * 0.1;
          const forceY = (-gradX / length) * safeVorticity * safeVorticityInfluence * 0.1;
          velocity[velIndex] += Math.max(-0.1, Math.min(0.1, forceX));
          velocity[velIndex + 1] += Math.max(-0.1, Math.min(0.1, forceY));
        }
      }
    }

    // Update water heights
    for (let i = 1; i < resolution - 1; i++) {
      for (let j = 1; j < resolution - 1; j++) {
        const index = i * resolution + j;
        const velIndex = index * 2;
        const top = previous[index - resolution];
        const bottom = previous[index + resolution];
        const left = previous[index - 1];
        const right = previous[index + 1];

        current[index] = (top + bottom + left + right) / 2 - current[index];
        current[index] = current[index] * damping + previous[index] * (1 - damping);
        current[index] += (0 - previous[index]) * safeTension;

        const velMagnitude = Math.sqrt(
          velocity[velIndex] * velocity[velIndex] +
          velocity[velIndex + 1] * velocity[velIndex + 1]
        );
        const safeVelInfluence = Math.min(velMagnitude * waterSettings.waveHeight, 0.1);
        current[index] += safeVelInfluence;
        current[index] *= 1.0 - densityDissipation * 0.01;
        current[index] = Math.max(-2.0, Math.min(2.0, current[index]));
      }
    }

    // Clear boundaries
    for (let i = 0; i < resolution; i++) {
      current[i] = 0;
      current[(resolution - 1) * resolution + i] = 0;
      velocity[i * 2] = 0;
      velocity[i * 2 + 1] = 0;
      velocity[((resolution - 1) * resolution + i) * 2] = 0;
      velocity[((resolution - 1) * resolution + i) * 2 + 1] = 0;
      current[i * resolution] = 0;
      current[i * resolution + (resolution - 1)] = 0;
      velocity[i * resolution * 2] = 0;
      velocity[i * resolution * 2 + 1] = 0;
      velocity[(i * resolution + (resolution - 1)) * 2] = 0;
      velocity[(i * resolution + (resolution - 1)) * 2 + 1] = 0;
    }

    // Swap buffers
    [waterBuffersRef.current.current, waterBuffersRef.current.previous] = 
      [waterBuffersRef.current.previous, waterBuffersRef.current.current];

    // Update texture
    if (waterTextureRef.current) {
      waterTextureRef.current.image.data = waterBuffersRef.current.current;
      waterTextureRef.current.needsUpdate = true;
    }
  };

  const addRipple = (x, y, strength = 1.0) => {
    if (!waterBuffersRef.current) return;
    
    const { resolution, rippleRadius } = waterSettings;
    const normalizedX = x / window.innerWidth;
    const normalizedY = 1.0 - y / window.innerHeight;
    const texX = Math.floor(normalizedX * resolution);
    const texY = Math.floor(normalizedY * resolution);
    const radius = Math.max(rippleRadius, Math.floor(waterSettings.rippleSize * resolution));
    const rippleStrength = strength * (waterSettings.splatForce / 100000);
    const radiusSquared = radius * radius;

    for (let i = -radius; i <= radius; i++) {
      for (let j = -radius; j <= radius; j++) {
        const distanceSquared = i * i + j * j;
        if (distanceSquared <= radiusSquared) {
          const posX = texX + i;
          const posY = texY + j;
          if (posX >= 0 && posX < resolution && posY >= 0 && posY < resolution) {
            const index = posY * resolution + posX;
            const velIndex = index * 2;
            const distance = Math.sqrt(distanceSquared);
            const falloff = 1.0 - distance / radius;
            const rippleValue = Math.cos((distance / radius) * Math.PI * 0.5) * rippleStrength * falloff;

            waterBuffersRef.current.previous[index] += rippleValue;

            const angle = Math.atan2(j, i);
            const velocityStrength = rippleValue * waterSettings.spiralIntensity;
            waterBuffersRef.current.velocity[velIndex] += Math.cos(angle) * velocityStrength;
            waterBuffersRef.current.velocity[velIndex + 1] += Math.sin(angle) * velocityStrength;

            const swirlAngle = angle + Math.PI * 0.5;
            const swirlStrength = Math.min(velocityStrength * 0.3, 0.1);
            waterBuffersRef.current.velocity[velIndex] += Math.cos(swirlAngle) * swirlStrength;
            waterBuffersRef.current.velocity[velIndex + 1] += Math.sin(swirlAngle) * swirlStrength;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    camera.position.z = 1;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(pixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Initialize water buffers
    waterBuffersRef.current = initWaterBuffers(waterSettings.resolution);

    // Create water texture
    const waterTexture = new THREE.DataTexture(
      waterBuffersRef.current.current,
      waterSettings.resolution,
      waterSettings.resolution,
      THREE.RedFormat,
      THREE.FloatType
    );
    waterTexture.minFilter = THREE.LinearFilter;
    waterTexture.magFilter = THREE.LinearFilter;
    waterTexture.needsUpdate = true;
    waterTextureRef.current = waterTexture;

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        u_time: { value: 0.0 },
        u_resolution: { value: new THREE.Vector2(window.innerWidth * pixelRatio, window.innerHeight * pixelRatio) },
        u_speed: { value: 1.3 },
        u_waterTexture: { value: waterTexture },
        u_waterStrength: { value: 0.55 },
        u_ripple_time: { value: -10.0 },
        u_ripple_position: { value: new THREE.Vector2(0.5, 0.5) },
        u_ripple_strength: { value: 0.5 },
        u_gradientTheme: { value: 0 }
      }
    });
    materialRef.current = material;

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      const elapsed = clockRef.current.getElapsedTime();
      material.uniforms.u_time.value = elapsed;
      updateWaterSimulation();
      renderer.render(scene, camera);
    };

    animate();

    // Add initial ripple
    setTimeout(() => {
      addRipple(window.innerWidth / 2, window.innerHeight / 2, 1.5);
    }, 500);

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const currentPixelRatio = Math.min(window.devicePixelRatio, 2);
      renderer.setSize(width, height);
      renderer.setPixelRatio(currentPixelRatio);
      material.uniforms.u_resolution.value.set(width * currentPixelRatio, height * currentPixelRatio);
    };

    const handleMouseMove = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const now = performance.now();

      if (now - mouseThrottleTimeRef.current < 8) return;
      mouseThrottleTimeRef.current = now;

      const dx = x - lastMousePositionRef.current.x;
      const dy = y - lastMousePositionRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const velocity = distance / 8;

      if (distance > 1) {
        const velocityInfluence = Math.min(velocity / 10, 2.0);
        const baseIntensity = Math.min(distance / 20, 1.0);
        const fluidIntensity = baseIntensity * velocityInfluence * waterSettings.mouseIntensity;
        const variation = Math.random() * 0.3 + 0.7;
        const finalIntensity = fluidIntensity * variation;

        const jitterX = x + (Math.random() - 0.5) * 3;
        const jitterY = y + (Math.random() - 0.5) * 3;

        addRipple(jitterX, jitterY, finalIntensity);

        lastMousePositionRef.current.x = x;
        lastMousePositionRef.current.y = y;
      }
    };

    const handleClick = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      addRipple(x, y, waterSettings.clickIntensity);

      const clickX = x / window.innerWidth;
      const clickY = 1.0 - y / window.innerHeight;
      material.uniforms.u_ripple_position.value.set(clickX, clickY);
      material.uniforms.u_ripple_time.value = clockRef.current.getElapsedTime();
    };

    const handleTouchMove = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const touch = event.touches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      const now = performance.now();

      if (now - mouseThrottleTimeRef.current < 8) return;
      mouseThrottleTimeRef.current = now;

      const dx = x - lastMousePositionRef.current.x;
      const dy = y - lastMousePositionRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 1) {
        const velocityInfluence = Math.min(distance / 80, 2.0);
        const baseIntensity = Math.min(distance / 20, 1.0);
        const fluidIntensity = baseIntensity * velocityInfluence * waterSettings.mouseIntensity;

        addRipple(x, y, fluidIntensity);

        lastMousePositionRef.current.x = x;
        lastMousePositionRef.current.y = y;
      }
    };

    const handleTouchStart = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const touch = event.touches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      addRipple(x, y, waterSettings.clickIntensity);

      const clickX = x / window.innerWidth;
      const clickY = 1.0 - y / window.innerHeight;
      material.uniforms.u_ripple_position.value.set(clickX, clickY);
      material.uniforms.u_ripple_time.value = clockRef.current.getElapsedTime();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchstart', handleTouchStart);
      
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      if (rendererRef.current && container) {
        container.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
    };
  }, [waterSettings, vertexShader, fragmentShader]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 w-full h-full"
      style={{ zIndex: 0 }}
    />
  );
};

export default LightModeCanvas;
